import React, { useState, useEffect, useMemo } from 'react';
import { versionToQuarter } from '@/utils/versionUtils';
import { useVersion } from '@/contexts/VersionContext';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { Version, setSelectedVersion } from '@/store/versionsSlice';
import { formatVersion } from '@/utils/versionsHelper';
import { Badge } from '@/components/ui/badge';
import { 
  Loader2, 
  Image as ImageIcon, 
  ExternalLink, 
  ChevronLeft, 
  ChevronRight,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface CompanyVersionsProps {
  slug: string;
  subslug?: string | null;
  selectedVersion?: string;
  versions?: any[];
}

const CompanyVersions: React.FC<CompanyVersionsProps> = ({ 
  slug, 
  subslug, 
  selectedVersion,
}) => {
  // Get versions from Redux store
  const reduxVersions = useAppSelector(state => state.versions.versions);
  const versionsLoading = useAppSelector(state => state.versions.loading);
  const company = useAppSelector(state => state.company.company);
  const { setSelectedVersion } = useVersion();
  const dispatch = useAppDispatch();
  
  // State for the toggle to show all versions or only ones with changes
  const [showAllVersions, setShowAllVersions] = useState(false);
  
  // State for the screenshot modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageVersion, setSelectedImageVersion] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  
  // Memoize sorted versions to avoid recalculation on every render
  const sortedVersions = useMemo(() => {
    return [...reduxVersions].sort((a, b) => 
      b.version.localeCompare(a.version)
    );
  }, [reduxVersions]);

  // Memoize filtered versions based on the toggle state
  const filteredVersions = useMemo(() => {
    if (showAllVersions) {
      return sortedVersions;
    } else {
      // Create a new array to hold filtered versions
      const filtered: Version[] = [];
      
      // Always include the most recent version
      if (sortedVersions.length > 0) {
        filtered.push(sortedVersions[0]);
      }
      
      // Go through the rest of the versions
      for (let i = 1; i < sortedVersions.length; i++) {
        const currentVersion = sortedVersions[i];
        
        // Include this version if it has changes or diffs
        if (currentVersion.changed || (currentVersion.diffs && currentVersion.diffs.length > 0)) {
          filtered.push(currentVersion);
        }
      }
      
      // Always include the oldest version if it's not already included
      if (sortedVersions.length > 1 && !filtered.includes(sortedVersions[sortedVersions.length - 1])) {
        filtered.push(sortedVersions[sortedVersions.length - 1]);
      }
      
      return filtered;
    }
  }, [sortedVersions, showAllVersions]);
  
  // Prepare version pairs for comparison (current version with its previous version)
  const versionPairs = useMemo(() => {
    const pairs = new Map();
    
    sortedVersions.forEach((version, index) => {
      // Get the previous version (if it exists)
      const previousVersion = index < sortedVersions.length - 1 
        ? sortedVersions[index + 1] 
        : null;
      
      pairs.set(version.version, previousVersion ? previousVersion.version : null);
    });
    
    return pairs;
  }, [sortedVersions]);
  
  // Function to find the screenshot URL for a specific version
  const getScreenshotUrlForVersion = (version: string): string | null => {
    if (!company || !company.pages) return null;
    
    // Find the relevant page based on subslug
    const relevantPage = subslug 
      ? company.pages.find(page => page.subslug === subslug)
      : company.pages.find(page => !page.subslug || page.subslug === '') || company.pages[0];
      
    if (!relevantPage) return null;
    
    // Check if the page has scrapes
    if (relevantPage.scrapes && Array.isArray(relevantPage.scrapes)) {
      // Find the scrape for this version
      const scrape = relevantPage.scrapes.find(s => s.version === version);
      if (scrape && scrape.screenshot_url) {
        return scrape.screenshot_url;
      }
    }
    
    // Fallback to the page's screenshot URL if it matches the version
    if (relevantPage.screenshot_url && relevantPage.screenshot_version === version) {
      return relevantPage.screenshot_url;
    }
    
    return null;
  };

  const handleVersionSelect = (version: string) => {
    if (setSelectedVersion) {
      setSelectedVersion(version);
      
      // Update the global selected version in Redux
      dispatch(setSelectedVersion(version));
      
      // Dispatch a custom event to notify all components (for backward compatibility)
      const versionChangeEvent = new CustomEvent('versionChange', { 
        detail: { version } 
      });
      window.dispatchEvent(versionChangeEvent);
    }
  };
  
  // Function to open the image modal
  const openImageModal = (version: string) => {
    const imageUrl = getScreenshotUrlForVersion(version);
    if (imageUrl) {
      // Find the index of this version in the filtered versions array
      const index = filteredVersions.findIndex(v => v.version === version);
      setSelectedImageIndex(index >= 0 ? index : 0);
      setSelectedImageUrl(imageUrl);
      setSelectedImageVersion(version);
      setZoomLevel(1); // Reset zoom level
      setIsImageModalOpen(true);
    }
  };
  
  // Function to navigate to the previous or next image
  const navigateImage = (direction: 'prev' | 'next') => {
    // Get the current versions array being displayed
    const versions = filteredVersions;
    if (!versions.length) return;
    
    // Get the current version
    const currentVersion = versions[selectedImageIndex];
    
    if (direction === 'next') {
      // If going forward, show the next version with changes
      if (selectedImageIndex < versions.length - 1) {
        const nextIndex = selectedImageIndex + 1;
        const nextVersion = versions[nextIndex].version;
        const nextImageUrl = getScreenshotUrlForVersion(nextVersion);
        
        if (nextImageUrl) {
          setSelectedImageIndex(nextIndex);
          setSelectedImageUrl(nextImageUrl);
          setSelectedImageVersion(nextVersion);
          setZoomLevel(1); // Reset zoom level
        }
      } else {
        // Wrap around to the first version
        const nextImageUrl = getScreenshotUrlForVersion(versions[0].version);
        
        if (nextImageUrl) {
          setSelectedImageIndex(0);
          setSelectedImageUrl(nextImageUrl);
          setSelectedImageVersion(versions[0].version);
          setZoomLevel(1); // Reset zoom level
        }
      }
    } else {
      // If going backward, we need to find the version this one is compared with
      const previousVersionId = versionPairs.get(currentVersion.version);
      
      if (previousVersionId) {
        // Find the index of the previous version in our filtered list
        const previousVersionIndex = versions.findIndex(v => v.version === previousVersionId);
        
        if (previousVersionIndex >= 0) {
          // If the previous version is in our filtered list, go to it
          const previousImageUrl = getScreenshotUrlForVersion(previousVersionId);
          
          if (previousImageUrl) {
            setSelectedImageIndex(previousVersionIndex);
            setSelectedImageUrl(previousImageUrl);
            setSelectedImageVersion(previousVersionId);
            setZoomLevel(1); // Reset zoom level
          }
        } else {
          // If the previous version is not in our filtered list, we need to find it in the full list
          const previousVersion = sortedVersions.find(v => v.version === previousVersionId);
          
          if (previousVersion) {
            const previousImageUrl = getScreenshotUrlForVersion(previousVersionId);
            
            if (previousImageUrl) {
              // Temporarily add this version to our navigation
              setSelectedImageUrl(previousImageUrl);
              setSelectedImageVersion(previousVersionId);
              // Don't update the index since this version isn't in our filtered list
              setZoomLevel(1); // Reset zoom level
            }
          } else {
            // If we can't find the previous version, just go to the previous in the filtered list
            const prevIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : versions.length - 1;
            const prevVersion = versions[prevIndex].version;
            const prevImageUrl = getScreenshotUrlForVersion(prevVersion);
            
            if (prevImageUrl) {
              setSelectedImageIndex(prevIndex);
              setSelectedImageUrl(prevImageUrl);
              setSelectedImageVersion(prevVersion);
              setZoomLevel(1); // Reset zoom level
            }
          }
        }
      } else {
        // If there's no previous version, just go to the previous in the filtered list
        const prevIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : versions.length - 1;
        const prevVersion = versions[prevIndex].version;
        const prevImageUrl = getScreenshotUrlForVersion(prevVersion);
        
        if (prevImageUrl) {
          setSelectedImageIndex(prevIndex);
          setSelectedImageUrl(prevImageUrl);
          setSelectedImageVersion(prevVersion);
          setZoomLevel(1); // Reset zoom level
        }
      }
    }
  };
  
  // Handle keyboard navigation in the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isImageModalOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          navigateImage('prev');
          break;
        case 'ArrowRight':
          navigateImage('next');
          break;
        case 'Escape':
          setIsImageModalOpen(false);
          break;
        case '+':
          setZoomLevel(prev => Math.min(prev + 0.25, 3));
          break;
        case '-':
          setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen, selectedImageIndex, filteredVersions]);

  // Loading state
  if (versionsLoading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Available Versions</h2>
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading versions...</span>
        </div>
      </div>
    );
  }

  // No versions state
  if (reduxVersions.length === 0) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Available Versions</h2>
        <p className="text-gray-500">No versions available for this company.</p>
      </div>
    );
  }

  // Main render
  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Available Versions</h2>
        
        <div className="flex items-center space-x-2">
          <Switch
            id="show-all-versions"
            checked={showAllVersions}
            onCheckedChange={setShowAllVersions}
          />
          <Label htmlFor="show-all-versions" className="text-sm">
            {showAllVersions ? 'Show all snapshots' : 'Show only versions with changes'}
          </Label>
        </div>
      </div>
      
      <div className="space-y-4">
        {filteredVersions.map((version: Version) => {
          const quarter = versionToQuarter(version.version);
          const isSelected = version.version === selectedVersion;
          
          return (
            <div 
              key={version.version}
              className={`p-4 border rounded-md cursor-pointer transition-colors ${
                isSelected 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => handleVersionSelect(version.version)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{formatVersion(version.version)}</span>
                    {quarter && <span className="text-sm text-gray-500">({quarter})</span>}
                    
                    {version.changed && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs py-0 px-1">
                        Changes Detected
                      </Badge>
                    )}
                    
                    {version.verified && (
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 text-xs py-0 px-1">
                        Verified
                      </Badge>
                    )}
                    
                    {isSelected && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 text-xs py-0 px-1">
                        Current
                      </Badge>
                    )}
                    
                    <button 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent version selection
                        openImageModal(version.version);
                      }}
                      className="ml-auto p-1 rounded-md hover:bg-gray-100 transition-colors"
                      title="View screenshot"
                    >
                      <ImageIcon className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  
                  {/* Display diffs if available */}
                  {version.diffs && version.diffs.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {/* Get the previous version from our pairs map */}
                      {(() => {
                        const previousVersionId = versionPairs.get(version.version);
                        const previousVersion = previousVersionId 
                          ? sortedVersions.find(v => v.version === previousVersionId)
                          : null;
                        
                        if (previousVersion) {
                          const prevQuarter = versionToQuarter(previousVersion.version);
                          return (
                            <p className="text-sm font-medium text-gray-700 mb-1">
                              Changes from {formatVersion(previousVersion.version)}
                              {prevQuarter && ` (${prevQuarter})`}:
                            </p>
                          );
                        } else {
                          return (
                            <p className="text-sm font-medium text-gray-700 mb-1">
                              Changes detected:
                            </p>
                          );
                        }
                      })()}
                      
                      <ul className="text-sm text-gray-600 pl-5 list-disc">
                        {version.diffs.map((diff, index) => (
                          <li key={index}>
                            <span className="font-medium">{diff.type}:</span> {diff.summary}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Show previous version information */}
                  {(() => {
                    const previousVersionId = versionPairs.get(version.version);
                    
                    if (previousVersionId) {
                      const previousVersion = sortedVersions.find(v => v.version === previousVersionId);
                      if (previousVersion) {
                        const prevQuarter = versionToQuarter(previousVersion.version);
                        return (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <p className="text-xs text-gray-500">
                              Compared with: {formatVersion(previousVersion.version)}
                              {prevQuarter && ` (${prevQuarter})`}
                            </p>
                          </div>
                        );
                      }
                    }
                    
                    return null;
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Show count of filtered vs. total versions */}
      <div className="mt-4 text-sm text-gray-500">
        Showing {filteredVersions.length} of {sortedVersions.length} versions
      </div>
      
      {/* Screenshot Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
        <DialogContent className="max-w-[90vw] w-[1200px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                Screenshot for {slug}{subslug ? `/${subslug}` : ''} - Version {selectedImageVersion ? formatVersion(selectedImageVersion) : ''}
                {selectedImageVersion && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({versionToQuarter(selectedImageVersion || '')})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setZoomLevel(prev => Math.max(prev - 0.25, 0.5))}
                  title="Zoom out"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm">{Math.round(zoomLevel * 100)}%</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setZoomLevel(prev => Math.min(prev + 0.25, 3))}
                  title="Zoom in"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto p-1 relative">
            {/* Navigation buttons */}
            <button 
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md z-10"
              onClick={() => navigateImage('prev')}
              title="Previous version"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            
            <button 
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md z-10"
              onClick={() => navigateImage('next')}
              title="Next version"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            
            {selectedImageUrl ? (
              <div className="relative flex justify-center">
                <img 
                  src={selectedImageUrl} 
                  alt={`Screenshot for ${slug} version ${selectedImageVersion}`}
                  className="h-auto object-contain transition-transform duration-200"
                  style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top center' }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                No screenshot available for this version
              </div>
            )}
          </div>
          
          <DialogFooter className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {selectedImageVersion && (
                <>
                  {formatVersion(selectedImageVersion)}
                  {versionToQuarter(selectedImageVersion) && ` (${versionToQuarter(selectedImageVersion)})`}
                  
                  {/* Show comparison info if available */}
                  {(() => {
                    // If this is a version in our filtered list
                    const versionIndex = filteredVersions.findIndex(v => v.version === selectedImageVersion);
                    if (versionIndex >= 0) {
                      return ` - ${versionIndex + 1} of ${filteredVersions.length}`;
                    }
                    return null;
                  })()}
                </>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              {selectedImageUrl && (
                <a 
                  href={selectedImageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in new tab
                </a>
              )}
              <Button onClick={() => setIsImageModalOpen(false)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyVersions;
